import { AbstractO2dElement, html, customElement, property, state } from '@o2d/shared';
import type { TemplateResult } from '@o2d/shared';
import { nothing, unsafeCSS } from 'lit';
import type { PropertyValues } from 'lit';
import tabListCss from '../tab-list.css?inline';
import type { O2dTab } from '../interfaces/tab.interface.js';
import type { TabSelectedDetail } from '../interfaces/event-detail.types.js';
import { O2D_TAB_LIST_SELECTED } from '../interfaces/event-detail.types.js';
import { resolveEffectiveActiveId } from '../controllers/active-id-resolver.js';
import { TabKeyboardController } from '../controllers/tab-keyboard.controller.js';

/**
 * `<o2d-tab-list>` — a lean, router-agnostic horizontal tab-strip Web Component.
 *
 * It manages the tab STRIP only: it renders tabs from a config, is
 * controlled-with-default over the active tab (`activeId`), applies user
 * interaction locally, and emits an `o2d-tab-list.selected` CustomEvent so the
 * consumer owns panel switching / route binding in its own view.
 *
 * Renders into an open SHADOW ROOT (`useShadowDom`, opt-in on
 * {@link AbstractO2dElement}). Its CSS ships with the element (`static styles`),
 * so nothing has to be imported. From outside the look is changed through
 * `--tab-list-*` custom properties and the parts `tablist`, `tab`,
 * `tab-selected`, `tab-disabled` and `tab-badge` (see `tab-list.css`); selectors
 * such as `o2d-tab-list [role="tab"]` no longer reach the inner nodes. Because the
 * label is rendered inside the shadow root, a `TemplateResult` label is not
 * touched by document-level CSS either — style such content inline or with
 * the custom properties it can inherit; a count belongs in the tab's `badge`.
 *
 * A subclass may set `useShadowDom = false` and render into its light DOM; keyboard
 * and focus handling work the same, but it then gets none of the packaged styles.
 *
 * Loop-safety invariant: `o2d-tab-list.selected` is dispatched ONLY from the
 * user-interaction paths (`selectTab`, driven by `_onTabClick` and the
 * `TabKeyboardController`) — NEVER from a reactive lifecycle hook
 * (`willUpdate`/`updated`) or a property setter. Setting the consumer-controlled
 * `activeId` therefore only recomputes the effective active id and re-renders; it
 * never echoes an event back. This is what lets a consumer bind `activeId` to its
 * own state without an event↔prop feedback loop.
 *
 * Selection precedence is temporal (last-write-wins between the two sources).
 * `activeId` is the consumer's controlled prop; `_localActiveId` is set by user
 * interaction. When the consumer pushes a NEW `activeId`, that external set is
 * authoritative and overrides any prior local selection (`_localSupersedesActiveId`
 * is reset in `willUpdate`). When the user then interacts locally, that local
 * selection in turn overrides the current `activeId` until the consumer pushes a
 * new one — so the strip stays usable even while a stale `activeId` is bound.
 *
 * Keyboard a11y (WAI-ARIA roving tabindex, Arrow/Home/End, automatic activation)
 * is delegated to {@link TabKeyboardController}; after every render the roving
 * `tabindex=0` tracks the EFFECTIVE active id, and DOM focus follows it ONLY when
 * focus already lives inside the strip (so an external `activeId` set never steals
 * focus away from elsewhere on the page).
 */
@customElement('o2d-tab-list')
export class O2dTabListElement extends AbstractO2dElement {

    static override useShadowDom = true;

    static override styles = unsafeCSS(tabListCss);

    /** Tab configuration to render, in display order. */
    @property({ attribute: false })
    tabs: O2dTab[] = [];

    /**
     * Consumer-controlled active tab id (controlled-with-default). When it
     * resolves to an enabled tab it overrides the element-internal selection.
     */
    @property()
    activeId?: string;

    /**
     * Element-internal active tab id, written by user interaction (click/keyboard)
     * so a local apply stays distinguishable from an external `activeId` set. Read
     * by the effective-active-id resolver; the first read/write pair lands with the
     * interaction handlers (T4.5+). Kept `@state()` so a local write re-renders.
     */
    @state()
    private _localActiveId?: string;

    /**
     * True once a local interaction has selected a tab more recently than the
     * consumer last pushed a new `activeId`. While true, the local selection
     * overrides the controlled `activeId`; a fresh external `activeId` set resets
     * it (see `willUpdate`). Kept `@state()` so a change re-renders.
     */
    @state()
    private _localSupersedesActiveId = false;

    /** Registers the keyboard controller (roving tabindex, Arrow/Home/End). */
    constructor() {
        super();
        new TabKeyboardController(this);
    }

    /**
     * The effective active tab id after applying the controlled-with-default
     * precedence and the temporal last-write-wins rule. Read by both `render`
     * (roving tabindex + aria-selected) and the keyboard controller (the id it
     * steps from). A local selection that supersedes `activeId` hides the
     * controlled id from the resolver so the local id wins.
     */
    get effectiveActiveId(): string | undefined {
        const controlledActiveId = this._localSupersedesActiveId ? undefined : this.activeId;
        return resolveEffectiveActiveId(this.tabs, controlledActiveId, this._localActiveId);
    }

    /**
     * A fresh external `activeId` set is authoritative: it overrides any prior
     * local selection. Resetting the supersede flag lets the resolver honour the
     * new controlled id again. This never emits an event (loop-safety).
     */
    protected override willUpdate(changed: PropertyValues<this>): void {
        if (changed.has('activeId')) {
            this._localSupersedesActiveId = false;
        }
    }

    /** Renders the tablist container with one `role="tab"` child per config entry. */
    protected override render(): TemplateResult {
        const activeId = this.effectiveActiveId;
        return html`
            <div role="tablist" part="tablist">
                ${this.tabs.map((tab) => this._renderTab(tab, activeId))}
            </div>
        `;
    }

    /**
     * Unifies DOM focus with the effective active id after every render (roving
     * focus). The roving `tabindex=0` is already set in `render`; here we move
     * actual focus ONLY when it already lives inside the strip, so an external
     * `activeId` set (or the initial mount) never steals focus from elsewhere.
     *
     * Focus inside a focusable element of a `TemplateResult` label also counts as
     * "inside the strip" (as in 0.1.2), so a re-render moves it onto the active tab.
     *
     * Tabs are looked up in `renderRoot`, which is the shadow root by default and the
     * element itself for a subclass that sets `useShadowDom = false`.
     */
    protected override updated(): void {
        const activeId = this.effectiveActiveId;
        if (activeId === undefined || !this._focusIsInside()) {
            return;
        }
        for (const tab of this.renderRoot.querySelectorAll<HTMLElement>('[role="tab"]')) {
            if (tab.getAttribute('data-id') === activeId) {
                tab.focus();
            }
        }
    }

    /**
     * Whether DOM focus currently lives inside the strip, in either render mode.
     *
     * The question is never asked of `document`: `document.activeElement` only names an
     * element of the document tree, so for a strip in a consumer's shadow root it would
     * answer with that consumer's host, never with a tab (the 0.1.1 bug reported by
     * open2done-invoice).
     * - With its own shadow root, that root's `activeElement` names the focused tab at
     *   any nesting depth.
     * - Without one (a subclass with `useShadowDom = false`), the tabs live in the
     *   element's own tree, so the root the element sits in (document or a consumer's
     *   shadow root) is asked, and the answer counts only if it lies inside the element.
     */
    private _focusIsInside(): boolean {
        if (this.shadowRoot) {
            return this.shadowRoot.activeElement !== null;
        }
        const root = this.getRootNode() as Document | ShadowRoot;
        const active = root.activeElement;
        return active !== null && this.contains(active);
    }

    /**
     * Renders a single tab. The label is a plain `string` (rendered as text) or a
     * Lit `TemplateResult` (rendered inline) — Lit handles both when interpolated.
     *
     * Controlled-with-default aria state: the effective active tab carries
     * `aria-selected="true"` and roving `tabindex="0"`; every other tab is
     * `aria-selected="false"` with `tabindex="-1"`. Because `activeId` always
     * resolves to an ENABLED tab (or `undefined`), a disabled tab is never the
     * active one, and a degenerate all-disabled/empty strip has no tab in the tab
     * order. A disabled tab is marked `aria-disabled="true"` and stays
     * non-interactive (its click is ignored in `_onTabClick`).
     */
    private _renderTab(tab: O2dTab, activeId: string | undefined): TemplateResult {
        const selected = tab.id === activeId;
        const part = ['tab', selected ? 'tab-selected' : '', tab.disabled ? 'tab-disabled' : '']
            .filter(Boolean)
            .join(' ');
        return html`
            <div
                role="tab"
                part="${part}"
                data-id="${tab.id}"
                aria-selected="${selected}"
                aria-disabled="${Boolean(tab.disabled)}"
                tabindex="${selected ? 0 : -1}"
                @click="${() => this._onTabClick(tab)}"
            >
                ${tab.label}${tab.badge === undefined
                    ? nothing
                    : html` <span class="badge" part="tab-badge">${tab.badge}</span>`}
            </div>
        `;
    }

    /**
     * Applies a tab selection from user interaction (the sole apply+emit path for
     * both click and keyboard). It writes the element-internal `_localActiveId`
     * (NEVER the consumer-controlled `activeId`), marks the local selection as
     * superseding the controlled id, and emits `o2d-tab-list.selected` so the
     * consumer owns panel/route switching. The event is dispatched ONLY from here,
     * never from a reactive lifecycle hook, so an external `activeId` set never
     * echoes back an event (loop-safety). The `id` is assumed to be an enabled tab
     * (the keyboard controller only resolves enabled ids; `_onTabClick` guards).
     */
    selectTab(id: string): void {
        this._localActiveId = id;
        this._localSupersedesActiveId = true;
        this.fireEvent<TabSelectedDetail>(O2D_TAB_LIST_SELECTED, { id });
    }

    /** Applies a click on an enabled tab; clicks on a disabled tab are ignored. */
    private _onTabClick(tab: O2dTab): void {
        if (tab.disabled) {
            return;
        }
        this.selectTab(tab.id);
    }

}
